import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '../api/client';
import { Button } from './Button';
import { Dialog } from './Dialog';
import styles from './ImageViewer.module.css';

/** Ein Bild der Ansicht. */
export interface ViewerItem {
  id: number;
  title: string;
}

/** Eigenschaften der Bildansicht. */
interface ImageViewerProps {
  /** Alle blätterbaren Bilder in Anzeigereihenfolge. */
  items: ViewerItem[];
  /** Index des angezeigten Bildes; `null` = geschlossen. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

/**
 * Große Bildansicht: zeigt das Original (bei Altbestandsbildern ohne Original ersatzweise das Vorschaubild),
 * blättert mit Schaltflächen oder den Pfeiltasten und bietet den Download des Originals an.
 * @param props Bilder, aktueller Index und Rückrufe.
 */
export function ImageViewer({ items, index, onIndexChange, onClose }: ImageViewerProps) {
  const { t } = useTranslation();
  const item = index === null ? null : (items[index] ?? null);
  // Merkt sich je Bild, ob das Original fehlt (dann wird das Vorschaubild gezeigt).
  const [noOriginal, setNoOriginal] = useState<Set<number>>(new Set());
  useEffect(() => {
    setNoOriginal(new Set());
  }, [items.length]);

  /** Blättert um `step` Bilder (begrenzt auf die Liste). */
  function move(step: number): void {
    if (index === null) return;
    const next = index + step;
    if (next >= 0 && next < items.length) onIndexChange(next);
  }

  // Pfeiltasten blättern, solange die Ansicht offen ist.
  useEffect(() => {
    if (index === null) return undefined;
    /** Reagiert auf Pfeil links und rechts. */
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'ArrowLeft' && index! > 0) onIndexChange(index! - 1);
      else if (event.key === 'ArrowRight' && index! < items.length - 1) onIndexChange(index! + 1);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [index, items.length, onIndexChange]);

  const original = item !== null && !noOriginal.has(item.id);
  let content: ReactNode = null;
  if (item) {
    const src = apiUrl(`/images/${item.id}/${original ? 'original' : 'thumb'}`);
    content = (
      <div>
        <figure className={styles.figure}>
          <img
            key={`${item.id}-${original ? 'o' : 't'}`}
            className={styles.image}
            src={src}
            alt={item.title}
            onError={() => setNoOriginal((current) => new Set(current).add(item.id))}
          />
        </figure>
        <div className={styles.bar}>
          <span className={styles.counter}>
            {t('viewer.position', { current: (index ?? 0) + 1, total: items.length })}
          </span>
          <span className={styles.group}>
            <Button variant="secondary" disabled={(index ?? 0) === 0} onClick={() => move(-1)}>
              {t('viewer.previous')}
            </Button>
            <Button
              variant="secondary"
              disabled={(index ?? 0) >= items.length - 1}
              onClick={() => move(1)}
            >
              {t('viewer.next')}
            </Button>
            {original && (
              <a href={apiUrl(`/images/${item.id}/original?download=1`)}>{t('viewer.download')}</a>
            )}
            <Button variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </span>
        </div>
      </div>
    );
  }
  return (
    <Dialog title={item?.title ?? ''} isOpen={item !== null} onClose={onClose} size="wide">
      {content}
    </Dialog>
  );
}
